<template>
  <div id="app">
    <el-container>
      <el-header class="app-header">
        <div class="header-content">
          <div class="brand">
            <span class="brand-mark">H</span>
            <div>
              <h1>康宝莱 CRM</h1>
              <p>客户健康档案管理</p>
            </div>
          </div>
          <div id="page-toolbar-actions" class="header-actions"></div>
        </div>
      </el-header>
      <el-main>
        <router-view />
      </el-main>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

onMounted(() => {
  document.documentElement.classList.toggle('dark', localStorage.getItem('theme') === 'dark')
})
</script>

<style>
:root {
  --bg: #f7f4ef;
  --bg-soft: #fbfaf7;
  --card: rgba(255, 255, 255, 0.86);
  --card-solid: #ffffff;
  --text: #2f332f;
  --text-soft: #71766d;
  --line: #ebe4d8;
  --accent: #7e9f85;
  --accent-soft: #edf4ec;
  --danger: #d96b6b;
  --shadow: 0 14px 40px rgba(96, 86, 69, 0.08);
  --radius: 18px;
}

:root.dark {
  --bg: #171916;
  --bg-soft: #20231f;
  --card: rgba(38, 42, 37, 0.9);
  --card-solid: #252922;
  --text: #ece8dd;
  --text-soft: #c1c8b8;
  --line: #373c34;
  --accent: #9fbd92;
  --accent-soft: #2d382b;
  --danger: #ef8888;
  --shadow: 0 18px 46px rgba(0, 0, 0, 0.25);
}

* { box-sizing: border-box; }
html, body, #app { min-height: 100%; }
body {
  margin: 0;
  background: var(--bg);
  color: var(--text);
  font-family: 'Source Han Sans SC', 'Noto Sans CJK SC', 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif;
}

#app {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(126, 159, 133, 0.13), transparent 34rem),
    linear-gradient(180deg, var(--bg-soft), var(--bg));
  color: var(--text);
}

.app-header.el-header {
  height: 72px;
  padding: 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(251, 250, 247, 0.78);
  color: var(--text);
  border-bottom: 1px solid var(--line);
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  box-shadow: 0 10px 30px rgba(96, 86, 69, 0.06);
}

:root.dark .app-header.el-header {
  background: rgba(23, 25, 22, 0.78);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);
}

.header-content {
  max-width: 1600px;
  height: 72px;
  margin: 0 auto;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 800;
  letter-spacing: 0.02em;
}

.brand h1 {
  margin: 0;
  font-size: 19px;
  line-height: 1.2;
  color: var(--text);
}

.brand p {
  margin: 3px 0 0;
  font-size: 12px;
  color: var(--text-soft);
}

.header-actions {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.el-main {
  padding: 96px 24px 24px;
  min-height: calc(100vh - 72px);
  background: transparent;
}

@media (max-width: 980px) {
  .app-header.el-header,
  .header-content {
    height: auto;
    min-height: 72px;
  }

  .header-content {
    padding: 12px 16px;
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .el-main {
    padding-top: 150px;
  }
}

.el-card,
.el-dialog,
.el-table,
.el-pagination,
.el-input__wrapper,
.el-select__wrapper,
.el-textarea__inner {
  transition: background-color .2s ease, color .2s ease, border-color .2s ease;
}

.el-card {
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  background: var(--card) !important;
  color: var(--text) !important;
  box-shadow: var(--shadow) !important;
}

.el-card__header { border-bottom: 1px solid var(--line) !important; }
.el-table { --el-table-header-bg-color: transparent; --el-table-bg-color: transparent; --el-table-tr-bg-color: transparent; --el-table-row-hover-bg-color: var(--accent-soft); color: var(--text); }
.el-table th.el-table__cell { background: transparent !important; color: var(--text-soft); }
.el-table td.el-table__cell, .el-table th.el-table__cell { border-bottom-color: var(--line) !important; }
.el-table__inner-wrapper::before { background: var(--line) !important; }
.el-input__wrapper, .el-select__wrapper, .el-textarea__inner { background: var(--card-solid) !important; border-color: var(--line) !important; box-shadow: 0 0 0 1px var(--line) inset !important; }
.el-input__inner, .el-textarea__inner { color: var(--text) !important; }
.el-button--primary { --el-button-bg-color: var(--accent); --el-button-border-color: var(--accent); --el-button-hover-bg-color: #6f9277; --el-button-hover-border-color: #6f9277; }
</style>
